import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../../store';
import { CategoryInCardView } from './CategoryInCardView';
import { formatValueWithoutCurrency } from '../../../../../helpers/currency.helper';
import { formatDateAndTime } from '../../../utils/date.utils';
import { WIDGET_CARD_STYLE } from '../../../styles';
import { ReactComponent as IconExpenseDefault } from '../../../../../assets/icons/svg/colored-icons/ic-colored-expense.svg';
import { ReactComponent as IconIncomeDefault } from '../../../../../assets/icons/svg/colored-icons/ic-colored-category-income.svg';
import { CategoryImage, CircleWrapper } from './GenericComponents';
import { ITransaction } from '../../../../../types';
import TooltipWithTriangle from '../../../../../generic-components/TooltipWithTriangle';
import { trackMixPanel } from '../../../../../services/mixpanel.service';

interface ICategorizationCardProps {
  transaction: ITransaction;
  transactionCategory: any;
  cardRef: any;
  className: string;
  showCategoryTooltip: boolean;
}

export const CategorizationCard = ({
  transaction,
  transactionCategory,
  cardRef,
  className,
  showCategoryTooltip,
}: ICategorizationCardProps) => {
  const { t } = useTranslation();
  const { merchantName, details, amount, timestamp } = transaction || {};
  const { categoryTitle, categoryImageName, isExpenseType, isIncomeType } = transactionCategory || {};
  const setActiveDrawerTransaction = useStore((state) => state.setActiveDrawerTransaction);
  const handleCategoryDropDownClick = () => {
    setActiveDrawerTransaction(transaction);
    trackMixPanel('Act on Choose Category on Card - Categorization');
  };
  return (
    <div
      className={`${WIDGET_CARD_STYLE} h-[222px] absolute left-[50%] translate-x-[-50%] translate-y-[7%] top-[-7%] ${className}`}
      ref={cardRef}
      data-testid='categorizationCard__wrapper'
    >
      <div className='w-full h-[44px] pt-[18px] pb-[14px] flex justify-center items-center border-b-[1px] border-bw03 relative'>
        <CategoryInCardView
          isCategoryChosen={!showCategoryTooltip}
          categoryTitle={categoryTitle}
          categoryImageName={categoryImageName}
          onCategoryDropDownClick={handleCategoryDropDownClick}
        />
        {showCategoryTooltip && (
          <TooltipWithTriangle
            tooltipContainerClassName='animate-bounceUp z-[21] absolute top-[50px] left-[15px] bg-bw08 rounded-[4px] w-[190px] h-[36px] flex justify-center items-center'
            tooltipContentClassName='text-bw00 font-metricRMedium text-[12px]'
            triangleClassName='z-0 absolute top-[-8px] left-[50%] ml-[-10px]'
            triangleClass='triangle-up'
          >
            {t('CATEGORIZATION_CATEGORIES.CHOOSE_CATEGORY_TOOLTIP')}
          </TooltipWithTriangle>
        )}
      </div>
      <div className='w-full h-full flex flex-col justify-between pt-[12px] pb-[15px] px-[24px] box-sizing'>
        <div className='flex flex-col items-center box-sizing'>
          <CircleWrapper>
            {!categoryTitle && isExpenseType && (
              <IconExpenseDefault data-testid='CategorizationCard__cat-expense-default-image' />
            )}
            {!categoryTitle && isIncomeType && (
              <IconIncomeDefault data-testid='CategorizationCard__cat-income-default-image' />
            )}
            {categoryTitle && categoryImageName && (
              <CategoryImage
                categoryImageName={categoryImageName}
                className='w-[32px] h-[32px]'
                testId='CategorizationCard__cat-image'
              />
            )}
          </CircleWrapper>
          <div
            data-testid='CategorizationCard__merchant-name'
            className='w-full mt-[10px] font-metricR text-bw07 text-sm leading-5 text-center truncate'
          >
            {merchantName || details}
          </div>
          <div
            className='mt-[6px] font-metricRBold text-bw07 text-md leading-5'
            data-testid='CategorizationCard__amount'
          >
            <span className='font-metricR text-md text-bw07 mr-[1px]'>$</span>
            {formatValueWithoutCurrency(Math.abs(amount), 2)}
          </div>
        </div>
        <div className='flex justify-center'>
          <div data-testid='CategorizationCard__date' className='font-metricR text-xxs text-bw07'>
            {formatDateAndTime(timestamp)}
          </div>
        </div>
      </div>
    </div>
  );
};
